import { isFromJdServices } from './../constants/index';
import { getUrlParam } from '.';
import * as native from './native';
import { Product } from '@/types/product';
import { cloneDeep } from 'lodash';
import dayjs from 'dayjs';
import {
  CLIENT_ID,
  PurchaseScene,
  SubscribeType,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { VipInfo } from '@/services/user/index.d';
import { CommodityConfig } from '@meitu/subscribe-core';
import { clientInfo } from '@meitu/pc-bridge';
import { PromotionType, isProVipLevel, isStandardVipLevel } from '@/constants/subscription';
import { getLocaleStore } from '@/i18n/localeStore';
import { getDefaultStore } from 'jotai';
import { teamInfoAtom } from '@/store';

const { $t } = getLocaleStore();

export function getIncomeSource() {
  const ua = navigator.userAgent.toLowerCase();
  const urlIncomeSource = getUrlParam('income_source');
  if (urlIncomeSource) return urlIncomeSource;
  if (ua.indexOf('mtxx-pc') > -1) {
    return 'MTXX-PC';
  } else if (ua.indexOf('mtxx-linux') > -1) {
    return 'MTXX-LINUX';
  } else if (ua.indexOf('mtxx-mac') > -1) {
    return 'MTXX-MAC';
  } else {
    // ua里不包含以上标识,又是web-pcxiuxiu.meitu.com域名，则默认是Windows端旧版本
    // 发现Windows 6.x旧版本有些会丢失掉income_sourcequery参数，ua又没有标识
    if (/web-pcxiuxiu(.*).meitu.com/.test(window.location.host)) {
      return 'MTXX-PC';
    }

    return 'MTXX-WEB';
  }
}

// 是否web端
export const isWeb = getIncomeSource() === 'MTXX-WEB';

/** 手机宽度上限（含）；与 UA 任一命中即视为 H5 */
export const MOBILE_H5_MAX_WIDTH = 768;

/** 手机 H5（与 isWeb 正交）：UA 命中手机，或视口宽度 ≤ 手机宽度 */
export function checkIsMobileH5(): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const byUa = /Android.+Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  return byUa;
}

export const isMobileH5 = checkIsMobileH5();

/** 全局 H5 样式标识，挂在 html 上，组件内用 :global(html.mobile-h5) 覆盖样式 */
export const MOBILE_H5_CLASS = 'mobile-h5';

if (typeof document !== 'undefined' && isMobileH5) {
  document.documentElement.classList.add(MOBILE_H5_CLASS);
}

// income_source字段，可选值0~4，表示：0-unknown 1-Windows 2-Mac 3-Linux 4-Web
export function getIncomeSourceValue() {
  const incomeSource = getIncomeSource();
  let value = 0;
  switch (incomeSource) {
    case 'MTXX-PC':
      value = 1;
      break;
    case 'MTXX-LINUX':
      value = 3;
      break;
    case 'MTXX-MAC':
      value = 2;
      break;
    case 'MTXX-WEB':
      value = 4;
      break;
    default:
      break;
  }

  return value;
}

export const getOs = () => {
  let platform = 'Web';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) {
    platform = 'Mac';
  } else if (ua.indexOf('windows') > -1) {
    platform = 'Windows';
  } else if (ua.includes('linux')) {
    platform = 'Linux';
  }
  return platform;
};

export const getClientId = () => {
  const ua = navigator.userAgent.toLowerCase();
  let clientId = '1189857397';
  if (ua.indexOf('mtxx-linux') > -1) {
    clientId = '1189857397';
  } else if (ua.indexOf('mtxx-pc') > -1) {
    clientId = '1189857397';
  } else if (ua.indexOf('mtxx-mac') > -1) {
    clientId = '1189857470';
  } else if (ua.indexOf('mtxx-web') > -1) {
    clientId = '1189857420';
  } else {
    // web端
    clientId = CLIENT_ID;
  }

  return clientId;
};

export const getTokenAndSet = async () => {
  let res = null;
  if (native.getLocalStorage) {
    res = await native.getLocalStorage('token');
  }
  if (res) {
    try {
      const result = typeof res === 'string' ? JSON.parse(res) : res;

      window.localStorage.setItem('access_token', result.access_token);
    } catch (e) {
      window.localStorage.removeItem('access_token');
    }
  } else {
    window.localStorage.removeItem('access_token');
  }
};

export const setToken = (res: any) => {
  native.setLocalStorage('token', typeof res === 'object' ? JSON.stringify(res) : res);

  // 本地缓存
  window.localStorage.setItem('access_token', res.access_token);
};

export const getGid = async () => {
  let gid = '';
  const incomeSource = getIncomeSource();
  if (incomeSource === 'MTXX-WEB') {
    gid = window?.mtstat?.getDeviceId() || '123';
    window.localStorage.setItem('GID', gid);
    return gid;
  }
  // 7.x版本的用gid，7.x以下的旧版本用Gid
  gid = getUrlParam('Gid') || getUrlParam('gid') || (await native.getGid()) || '123';

  window.localStorage.setItem('GID', gid);
  return gid;
};

// 关闭vip购买窗口
export function closeVipSubscribeDialog(data?: any) {
  if (getIncomeSource() === 'MTXX-WEB') {
    // 被frame到其他页面，尝试发送消息通知关闭窗口
    if (window.top !== window.self) {
      window.parent?.postMessage(
        JSON.stringify({
          action: 'closeVipSubscribeDialog',
          data,
        }),
        decodeURIComponent(getUrlParam('callback_origin') || window.location.origin)
      );
    }
  } else {
    native.closeVipSubscribeDialog && native.closeVipSubscribeDialog();
  }
}
// 兑换成功回调
export function handleExchangeSuccess() {
  if (getIncomeSource() === 'MTXX-WEB') {
    // 被frame到其他页面，尝试发送消息通知兑换成功
    if (window.top !== window.self) {
      window.parent?.postMessage(
        JSON.stringify({
          action: 'exchangeSuccess',
          data: {},
        }),
        decodeURIComponent(getUrlParam('callback_origin') || window.location.origin)
      );
    }
  } else {
  }
}

// 判断是否来自Mac端Safari内核 --- 通信方式
export const isFromMacSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf('usesafari') > -1) {
    return true;
  }

  return false;
};

// 是否苹果支付 苹果支付就不展示支付宝和微信tab
export const isAppStorePay = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf('forappstore') > -1) {
    return true;
  }

  return false;
};

/**
 * 获取主题色
 * @returns
 */
export const getTheme = async () => {
  // web端从缓存中取
  let theme;

  if (getIncomeSource() !== 'MTXX-WEB') {
    theme = getUrlParam('theme') || (await native.getTheme());
  } else {
    theme = getUrlParam('theme') || window.localStorage.getItem('theme');
  }

  if (theme === 'dark') {
    return 'dark';
  }

  return 'light';
};

/**
 * 获取商品支付渠道
 * 如果有优惠价优先展示优惠价对应的支付去掉，否则才展示商品实际支付渠道
 * @param data 商品信息
 * @returns
 */
export const getPayChannels = (data?: Product) => {
  if (!data) return [];

  const productInfo = cloneDeep(data);
  // 取第一个优惠信息
  const promotionInfo = productInfo.promotions && productInfo.promotions[0];

  const channels = promotionInfo?.payChannels?.length
    ? promotionInfo.payChannels
    : productInfo.payChannels || [];
  return channels.filter(
    (item: string) => !isFromJdServices || (isFromJdServices && item !== 'alipay')
  );
};

/**
 * 获取商品最终的展示价
 * 如果有优惠价优先展示优惠价，否则才展示商品实际价
 * @param data 商品信息
 * @returns
 */
export const getDisplayPrice = (data?: Product) => {
  if (!data) return {};

  const productInfo = cloneDeep(data);
  // 取第一个优惠信息
  const promotionInfo = productInfo.promotions && productInfo.promotions[0];

  const originalPrice = productInfo.productPrice?.originalPrice;
  const actualPrice = productInfo.productPrice?.price;

  if (promotionInfo) {
    if (!promotionInfo.promotionPrice) promotionInfo.promotionPrice = {};

    // 如果优惠价格为0，则取实际价格，同时单位也要取实际价对应的单位
    promotionInfo.promotionPrice.moneySymbol =
      promotionInfo.promotionPrice?.moneySymbol && promotionInfo.promotionPrice?.price
        ? promotionInfo.promotionPrice.moneySymbol
        : productInfo.productPrice.moneySymbol;

    // 价格转换成元
    promotionInfo.promotionPrice.price = promotionInfo.promotionPrice?.price
      ? promotionInfo.promotionPrice.price / 100
      : (actualPrice || 0) / 100;

    // 支付价，即活动价
    promotionInfo.promotionPrice.payPrice = promotionInfo.promotionPrice.price;

    // 如果是免费试用/低价类型，商品价格取售卖价
    // promotion_type优惠类型：1：推介促销-首期优惠 2：推介促销-免费试用 3：促销优惠-随用随付 4：促销优惠-免费试用 5：促销优惠-提前支付 6：买赠活动 11：推介促销-低价试用 12：促销优惠-低价试用 17：阶梯优惠
    // 2, 4, 11, 12
    if (
      [
        PromotionType.RecommendedFreeTrial,
        PromotionType.SalesFreeTrial,
        PromotionType.LowPriceTrialPromotion,
        PromotionType.LowPriceTrialPromotionOffers,
      ].includes(promotionInfo.promotionType)
    ) {
      promotionInfo.promotionPrice.price = (actualPrice || 0) / 100;
    }

    if (originalPrice) {
      promotionInfo.promotionPrice.originalPrice = originalPrice / 100;
    }

    return promotionInfo.promotionPrice;
  }

  if (!productInfo.productPrice) productInfo.productPrice = {};
  // 价格转换成元
  productInfo.productPrice.price = (actualPrice || 0) / 100;
  // 支付价，即实际价
  productInfo.productPrice.payPrice = productInfo.productPrice.price;

  if (originalPrice) {
    productInfo.productPrice.originalPrice = originalPrice / 100;
  }
  return productInfo.productPrice;
};

/**
 * 团队版会员商品支付价（含席位数、阶梯优惠）
 * - 阶梯优惠：promotion_price * enjoy_num + (seats - enjoy_num) * product_price
 * - 非阶梯优惠：payPrice * seats
 */
export const getTeamSubscriptionPayPrice = (product?: Product, seats = 1) => {
  if (!product) return undefined;

  const { payPrice = 0 } = getDisplayPrice(product);
  const promotionInfo: any = product.promotions?.[0];
  const isTieredDiscounts = promotionInfo?.promotionType === PromotionType.TieredDiscounts;

  if (isTieredDiscounts) {
    const enjoyNum = promotionInfo?.enjoyNum || 0;
    const productPrice = (product.productPrice?.price || 0) / 100;
    return +((payPrice || 0) * enjoyNum + (seats - enjoyNum) * productPrice).toFixed(2);
  }

  return +((payPrice || 0) * seats).toFixed(2);
};

/**
 * 获取用户会员标识
 * SVIP级别高于粉砖VIP，所以优先展示SVIP
 * @param vipInfo
 * @returns
 */
export const getVipTag = (vipInfo?: VipInfo) => {
  // SVIP：普通会员（vip_level 为 1 或 2）
  const svip = isTeamSubscription
    ? vipInfo?.is_vip
    : vipInfo?.is_vip && isStandardVipLevel(vipInfo?.vip_level);

  // 粉砖VIP：美图秀秀会员
  const vip = vipInfo?.is_xx_vip;

  // 高级会员：vip_level 为 3
  const proVip = isTeamSubscription ? false : vipInfo?.is_vip && isProVipLevel(vipInfo?.vip_level);

  // 从未购买过
  const hasNoBuy = vipInfo?.vip_days === 0;

  return {
    svip,
    vip,
    proVip,
    hasNoBuy,
  };
};

/**
 * 获取会员展示文案
 * @param vipInfo
 * @returns
 */
export const getVipDesc = (vipInfo?: VipInfo) => {
  if (!vipInfo) return '';

  const vipStatus = getVipTag(vipInfo);

  // SVIP会员
  if (vipStatus.svip || vipStatus.proVip) {
    const expireTime = dayjs((vipInfo.vip_end_time || 0) * 1000).format('YYYY/MM/DD HH:mm');
    if (isTeamSubscription) {
      // 团队会员等级：1 团队版、2 高级团队版、3 专业团队版
      const teamLevel = getDefaultStore().get(teamInfoAtom)?.vip_level ?? 0;
      if (teamLevel === 2) {
        return $t((v, { r }) => r(v.Text_SeniorTeamMemberExpires, [expireTime]));
      }
      if (teamLevel === 3) {
        return $t((v, { r }) => r(v.Text_ProfessionalTeamMemberExpires, [expireTime]));
      }
      return $t((v, { r }) => r(v.Text_TeamMemberExpires, [expireTime]));
    }
    if (isWeb) {
      if (vipStatus.proVip) {
        return $t((v, { r }) => r(v.Text_ProMemberExpires, [expireTime]));
      }
      return $t((v, { r }) => r(v.Text_BasicMemberExpires, [expireTime]));
    }
    return $t((v, { r }) => r(v.Text_SvipMemberExpires_MeituXiuXiu, [expireTime]));
  } else {
    // 非SVIP会员

    // 粉钻会员（非会员）
    if (vipStatus.vip) {
      return isWeb
        ? $t((v) => v.Text_ActivateVip_DesignKit)
        : $t((v) => v.Text_UpgradeToSvip_MeituXiuXiu);
    }

    // 从未购买过（非会员）
    if (vipInfo?.vip_days !== undefined && vipInfo?.vip_days === 0) {
      return isTeamSubscription
        ? $t((v) => v.Text_ActivateTeamMember_DesignKit)
        : isWeb
        ? $t((v) => v.Text_ActivateMember)
        : $t((v) => v.Text_ActivateMember_MeituXiuXiu);
    }

    // 已过期（非会员）
    if (vipInfo?.vip_days && vipInfo?.vip_days < 0) {
      const expiredDays = Math.abs(vipInfo?.vip_days);
      return isTeamSubscription
        ? $t((v, { r }) => r(v.Text_ExpiredDays_Team, [expiredDays]))
        : isWeb
        ? $t((v, { r }) => r(v.Text_VipExpiredDays_DesignKit, [expiredDays]))
        : $t((v, { r }) => r(v.Text_VipExpiredDays_MeituXiuXiu, [expiredDays]));
    }
  }

  return '';
};

// 获取张数商品的有效期文案
export const getDurationPeriod = (data: CommodityConfig) => {
  if (!data) return;

  // 物料时限类型(1：永久计量型，2：期限计量型')
  if (data.limitType === 1) {
    return $t((v) => v.Text_PermanentValid);
  } else {
    switch (data.period) {
      case 1:
        return $t((v, { r }) => r(v.Text_ValidDays, [data.duration]));
      case 2:
        return $t((v, { r }) => r(v.Text_ValidWeeks, [data.duration]));
      case 3:
        return $t((v, { r }) => r(v.Text_ValidMonths, [data.duration]));
      case 4:
        return $t((v, { r }) => r(v.Text_ValidQuarters, [data.duration]));
      case 5:
        return $t((v, { r }) => r(v.Text_ValidYears, [data.duration]));
      default:
    }
  }
};

// 获取会员类型的周期的文案
export const getSubscriptionDurationPeriodText = (period: number) => {
  switch (period) {
    case 0:
      return $t((v) => v.Text_UnitDay);
    case 1:
      return $t((v) => v.Label_Week);
    case 2:
      return $t((v) => v.Month_Plan_Pricing);
    case 3:
      return $t((v) => v.Label_Quarter);
    case 4:
      return $t((v) => v.Label_Year);
    default:
      return '';
  }
};

export const getDeviceType = async () => {
  return clientInfo.isClient ? 2 : 3;
};

export const getOperaSystem = () => {
  // 操作系统：0-未知 1-Android、2-AndroidPad、3-iOS、4-Windows、5-Linux、6-iPadOS、7-macOS
  let oper_system = 0;
  if (/Android/i.test(navigator.userAgent) && !/Tablet/i.test(navigator.userAgent)) {
    // AndroidPad操作系统
    oper_system = 1;
  } else if (/Android/i.test(navigator.userAgent) && /Tablet/i.test(navigator.userAgent)) {
    // AndroidPad操作系统
    oper_system = 2;
  } else if (/iOS/i.test(navigator.userAgent)) {
    // iOS操作系统
    oper_system = 3;
  } else if (/Windows/i.test(navigator.userAgent)) {
    // Windows操作系统
    oper_system = 4;
  } else if (/Linux/i.test(navigator.userAgent)) {
    // Linux操作系统
    oper_system = 5;
  } else if (/iPad/i.test(navigator.userAgent)) {
    // iPadOS
    oper_system = 6;
  } else if (/Macintosh|Mac OS X/i.test(navigator.userAgent)) {
    // macOS操作系统
    oper_system = 7;
  }
  return oper_system;
};

/**
 * 团队版下，区分权益类型
 * first_payment 首次订阅
 * seat 加购席位
 * equity_purchase 权益加购（购买美豆/张数）
 * @returns
 */
export const getEquityValue = (vipInfo?: VipInfo) => {
  if (!isTeamSubscription) {
    return '';
  }

  // 购买美豆/张数
  if ([SubscribeType.MEIDOU, SubscribeType.CONSUMABLE].includes(subscribeType)) {
    return 'equity_purchase';
  }

  // 加购
  if (subscribeType === SubscribeType.SUBSCRIPTION && purchaseScene === PurchaseScene.EXTRA) {
    return 'seat';
  }

  // 首次购买会员
  if (vipInfo?.vip_days !== undefined && vipInfo?.vip_days === 0) {
    return 'first_payment';
  }

  // 续费
  return 'renew';
};
